/**
 * My Pantry global search (Pantry.jsx).
 *
 * The server is the authority on a new item's identity (POST /pantry/items):
 * a tapped suggestion keeps its Core Food, typed text is resolved to a Core
 * Food when it safely can, otherwise saved as a custom item. These helpers only
 * drive the search UI and the client-side duplicate / double-tap guards.
 * Duplicate checks span all five sections: canonical food_id first,
 * normalized name only for entries that are not canonical Core items (older
 * or custom entries).
 */
export var PANTRY_SECTIONS = ['produce', 'proteins', 'dairy', 'pantry', 'other'];

export function normalizePantryName(name) {
  return String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Identities already in the Pantry, across ALL five sections. */
export function ownedPantryIdentity(pantryItems) {
  var ids = new Set();
  var names = new Set();
  PANTRY_SECTIONS.forEach(function(section) {
    ((pantryItems && pantryItems[section]) || []).forEach(function(item) {
      if (!item) return;
      if (item.food_id) ids.add(String(item.food_id));
      var key = normalizePantryName(item.name);
      if (item.source !== 'core' && key) names.add(key);
    });
  });
  return { ids: ids, names: names };
}

export function isAlreadyInPantry(result, owned) {
  if (!result) return false;
  if (result.food_id && owned.ids.has(String(result.food_id))) return true;
  var key = normalizePantryName(result.display_name || result.name);
  return !!key && owned.names.has(key);
}

/** True when any item in any section already uses this name. */
export function hasItemNamed(pantryItems, name) {
  var key = normalizePantryName(name);
  if (!key) return false;
  return PANTRY_SECTIONS.some(function(section) {
    return ((pantryItems && pantryItems[section]) || []).some(function(item) {
      return !!item && normalizePantryName(item.name) === key;
    });
  });
}

/**
 * In-flight double-tap keys for one add request ({name, food_id?}). A tapped
 * suggestion and the typed row for the same name share the name key, so they
 * can't both be sent.
 */
export function pantryAddLockKeys(payload) {
  var keys = ['name:' + normalizePantryName(payload && payload.name)];
  if (payload && payload.food_id) keys.push('core:' + payload.food_id);
  return keys;
}

/**
 * What the search area shows for a query (in every search state):
 *   results — Core Food suggestions the user doesn't have yet (empty while
 *             loading or after a failed search);
 *   typed   — the always-available typed-value row: 'add', or 'already' when
 *             an item of that exact name is already in the Pantry.
 * Suggestions never gate saving: the typed row sends the text and the server
 * resolves it to a Core Food when it safely can.
 */
export function pantrySearchView(query, results, pantryItems) {
  var owned = ownedPantryIdentity(pantryItems);
  var visible = (results || []).filter(function(result) { return !isAlreadyInPantry(result, owned); });
  return {
    results: visible,
    typed: { kind: hasItemNamed(pantryItems, query) ? 'already' : 'add' },
  };
}
